import { useId, useState } from 'react';
import { NotePencil, Plus, Trash } from '@phosphor-icons/react';
import { BestaetigenDialog, ImmoButton, InnenKarte, LeerZustand, ToastPill, useToastPill } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import type { NotizZeile } from '../../../../../lib/immobilien/useObjektDetail';
import { useObjektSeite } from '../ObjektSeitenKontext';

const DATUM = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** Panel „Notizen“ zum Objekt (Tabelle notizen, objekt_id) – anlegen und löschen wie bisher. */
export function NotizenPanel() {
  const { daten, neuLaden } = useObjektSeite();
  return <NotizenListe notizen={daten.notizen} ziel={{ objekt_id: daten.objekt.id }} platzhalter="Notiz zu diesem Objekt …" onGeaendert={() => neuLaden('notizen')} />;
}

/** Notizen anlegen und löschen – für Objekt (objekt_id) und Einheit (einheit_id). */
export function NotizenListe({ notizen, ziel, platzhalter, onGeaendert }: {
  notizen: readonly NotizZeile[];
  ziel: { objekt_id: string } | { einheit_id: string };
  platzhalter: string;
  onGeaendert: () => void;
}) {
  const [toast, zeige] = useToastPill();
  const [entwurf, setEntwurf] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [loeschen, setLoeschen] = useState<NotizZeile | null>(null);
  const feldId = useId();

  const speichere = async () => {
    const text = entwurf.trim();
    if (!text) return;
    setLaeuft(true);
    const { error } = await supabase.from('notizen').insert({ ...ziel, text });
    setLaeuft(false);
    if (error) { zeige(`Notiz nicht gespeichert: ${error.message}`, true); return; }
    setEntwurf('');
    zeige('Notiz gespeichert');
    onGeaendert();
  };

  const bestaetigeLoeschen = async () => {
    if (!loeschen) return;
    setLaeuft(true);
    const { error } = await supabase.from('notizen').delete().eq('id', loeschen.id);
    setLaeuft(false);
    if (error) { zeige(`Löschen fehlgeschlagen: ${error.message}`, true); return; }
    setLoeschen(null);
    zeige('Notiz gelöscht');
    onGeaendert();
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte>
        <label htmlFor={feldId} style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Neue Notiz</label>
        <textarea id={feldId} value={entwurf} onChange={e => setEntwurf(e.target.value)} rows={3} placeholder={platzhalter}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void speichere(); } }}
          style={{ width: '100%', resize: 'vertical', border: 'none', outline: 'none', borderRadius: 16, padding: 14, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, lineHeight: 1.5, color: 'var(--ink)' }} />
        <ImmoButton art="akzent" groesse={44} pille={false} icon={Plus} onClick={() => { void speichere(); }} disabled={!entwurf.trim() || laeuft} style={{ alignSelf: 'flex-end' }}>
          Notiz speichern
        </ImmoButton>
      </InnenKarte>
      {notizen.length === 0 && <LeerZustand icon={NotePencil} titel="Noch keine Notizen" />}
      {notizen.map(n => (
        <div key={n.id} className="immo-innen flex items-start" style={{ borderRadius: 20, padding: '14px 16px', gap: 12 }}>
          <NotePencil weight="fill" size={20} color="var(--warn)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div className="flex flex-col flex-1 min-w-0" style={{ gap: 4 }}>
            <span style={{ fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.text}</span>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{DATUM.format(new Date(n.created_at))}</span>
          </div>
          <button type="button" aria-label="Notiz löschen" onClick={() => setLoeschen(n)} className="flex items-center justify-center cursor-pointer"
            style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
            <Trash size={16} />
          </button>
        </div>
      ))}
      {loeschen && (
        <BestaetigenDialog titel="Notiz löschen?" text="Das kann nicht rückgängig gemacht werden." detail={loeschen.text.slice(0, 160)}
          bestaetigenLabel="Löschen" laeuft={laeuft} onBestaetigen={() => { void bestaetigeLoeschen(); }} onAbbrechen={() => setLoeschen(null)} />
      )}
    </>
  );
}
